import React, { useState } from 'react';
import { Card, CardHeader, Heading, CardBody, useColorModeValue, Box, Text } from '@chakra-ui/react';
import ReactApexChart from 'react-apexcharts';
import { RotateCw } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { getTransactionSizeDistributionAnalysis } from '../../../../redux/actions/dashboard';

const TransactionSizeDistributionChart = ({ data, title = 'Transaction Size Distribution', height = 400, financialAnalysisDateRange, user }) => {
   // Color mode values must be declared at the top
   const textColor = useColorModeValue('gray.800', 'white');
   const chartBg = useColorModeValue('white', 'gray.700');
   const gridColor = useColorModeValue('#E2E8F0', '#4A5568');
   const tooltipBg = useColorModeValue('#FFFFFF', '#2D3748');
   const tooltipTextColor = useColorModeValue('#1A202C', '#F7FAFC');
   const countColor = useColorModeValue('#3182CE', '#63B3ED'); // Blue
   const amountColor = useColorModeValue('#38A169', '#68D391'); // Green
   const isDark = useColorModeValue(false, true);
   const [isRotating, setIsRotating] = useState(false);
   const dispatch = useDispatch();

   if (!data || data.length === 0) {
      return (
         <Card h={height} bg={chartBg}>
            <CardHeader>
               <Heading size="md" color={textColor}>
                  {title}
               </Heading>
            </CardHeader>
            <CardBody>
               <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Text color={textColor}>No data available</Text>
               </Box>
            </CardBody>
         </Card>
      );
   }

   const categories = data.map((item) => item.amount_bucket);
   const countPercentage = data.map((item) => parseFloat(item.count_percentage));
   const amountPercentage = data.map((item) => parseFloat(item.amount_percentage));

   const tooltipDetails = data.map((item) => ({
      avgAmount: item.avg_amount,
      transactionCount: item.transaction_count,
      totalAmount: item.total_amount,
   }));

   const options = {
      chart: {
         type: 'bar',
         stacked: true,
         toolbar: { show: false },
         background: chartBg,
         foreColor: textColor,
      },
      plotOptions: {
         bar: {
            horizontal: false,
            columnWidth: '45%',
            borderRadius: 5,
         },
      },
      xaxis: {
         categories,
         title: {
            text: 'Amount Bucket',
            style: {
               color: textColor,
            },
         },
         labels: {
            rotate: -45,
            style: {
               colors: textColor,
            },
         },
         axisBorder: {
            color: gridColor,
         },
         axisTicks: {
            color: gridColor,
         },
      },
      yaxis: {
         title: {
            text: 'Percentage (%)',
            style: {
               color: textColor,
            },
         },
         max: 100,
         labels: {
            style: {
               colors: textColor,
            },
         },
      },
      tooltip: {
         shared: false,
         custom: ({ seriesIndex, dataPointIndex }) => {
            const d = tooltipDetails[dataPointIndex];
            return `
          <div style="padding:8px; background:${tooltipBg}; color:${tooltipTextColor}; border-radius:4px">
            <strong>Avg Txn:</strong> ₹${d.avgAmount}<br/>
            <strong>Count:</strong> ${d.transactionCount}<br/>
            <strong>Total:</strong> ₹${d.totalAmount.toLocaleString('en-IN')}
          </div>
        `;
         },
      },
      colors: [countColor, amountColor],
      legend: {
         position: 'top',
         horizontalAlign: 'center',
         labels: {
            colors: textColor,
         },
      },
      dataLabels: {
         enabled: true,
         formatter: (val) => `${val.toFixed(1)}%`,
         style: {
            colors: [textColor],
         },
      },
      grid: {
         borderColor: gridColor,
         strokeDashArray: 4,
         xaxis: {
            lines: {
               show: false,
            },
         },
         yaxis: {
            lines: {
               show: true,
            },
         },
      },
   };

   const series = [
      {
         name: 'Count %',
         data: countPercentage,
      },
      {
         name: 'Amount %',
         data: amountPercentage,
      },
   ];

   const handleReload = async () => {
      setIsRotating(true);
      dispatch(
         getTransactionSizeDistributionAnalysis({
            companyId: user?.loginhistory?.companyId,
            fromDate: financialAnalysisDateRange.fromDate,
            toDate: financialAnalysisDateRange.toDate,
         }),
      );
      setTimeout(() => setIsRotating(false), 700);
   };

   return (
      <Card h={height} bg={chartBg}>
         <CardHeader display="flex">
            <Heading size="md" color={textColor}>
               {title}
            </Heading>
            <button
               onClick={handleReload}
               className={`ml-auto p-2 rounded-full transition-all hover:scale-110 ${isDark ? "hover:bg-gray-600 text-blue-400" : "hover:bg-gray-100 text-blue-600"
                  }`}
            >
               <RotateCw
                  className={`w-5 h-5 transform ${isRotating ? "rotate-[360deg]" : ""
                     } transition-transform ease-in-out`}
                  style={{
                     transitionDuration: isRotating ? "700ms" : "10ms",
                  }}
               />
            </button>
         </CardHeader>
         <CardBody>
            <ReactApexChart options={options} series={series} type="bar" height={height - 80} />
         </CardBody>
      </Card>
   );
};

export default TransactionSizeDistributionChart;
